import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputLabel,
  TextField,
} from '@mui/material';
import React, { memo } from 'react';
import { CloseIcon } from '~/assets/icons';
import { useFormik } from 'formik';
import { DIALOG_MODE_EDIT } from '~/utils/constants/common';
import { handleApiError, notify } from '~/utils/helper';
import { BaseRow } from '~/components/common';
import { useTranslation } from 'react-i18next';
import { IProfile, IUpdateProfile } from '~/utils/interface/auth';
import { useAppDispatch } from '~/redux/hooks';
import { handleEditSetting } from '~/thunks/auth/authThunk';
import { accountActions } from '~/thunks/account/accountSlice';

type EditAccountSettingDialogProps = {
  open: boolean;
  setOpen?: (open: boolean, needRefreshData?: boolean) => void;
  mode?: string;
  title?: string;
  data?: IProfile;
  children?: React.ReactNode;
};

const initialValues: IUpdateProfile = {
  oldPassword: '',
  password: '',
  confirmPassword: '',
};

const AccountSettingPasswordDialog = (props: EditAccountSettingDialogProps) => {
  //#region Destructuring Props
  const { mode } = props;
  //#endregion Destructuring Props
  const dispatch = useAppDispatch();
  //#region Declare Hook
  const { t } = useTranslation(['admin/account', 'admin/common']);
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  //#endregion Declare State
  //#region Implement Hook

  //#endregion Implement Hook
  //#region Handle Function

  const handleSetOpen = (open: boolean, needRefreshData?: boolean) => {
    if (props.setOpen) {
      props.setOpen(open, needRefreshData);
    }
    form.setValues({
      oldPassword: '',
      password: '',
      confirmPassword: '',
    });
  };

  const handleCancel = () => {
    handleSetOpen(false);
    form.resetForm();
  };
  const form = useFormik({
    validateOnChange: false,
    initialValues,
    onSubmit: async (values, { setFieldError }) => {
      if (mode === DIALOG_MODE_EDIT) {
        try {
          await dispatch(handleEditSetting({ params: values })).unwrap();
          dispatch(accountActions.changeNeedRefreshData(true));
          notify('success', t('editSuccess', { ns: 'admin/common' }));
          handleSetOpen(false, true);
          form.resetForm();
        } catch (e: unknown) {
          handleApiError(e, setFieldError);
        }
        return;
      }
    },
  });
  //#endregion Handle Function

  return (
    <Dialog open={props.open} onClose={() => handleSetOpen(false)} fullWidth maxWidth='md'>
      <DialogTitle>
        {t('settingAccount.changePassword')}
        <IconButton onClick={() => handleSetOpen(false)} className='absolute right-[10px] top-[10px]'>
          <CloseIcon className='w-[10px] h-[10px]' />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Box component='form'>
          <BaseRow className='gap-[20px]'>
            <Box className='w-full'>
              <InputLabel> {t('settingAccount.oldPassword')}</InputLabel>
              <TextField
                name='oldPassword'
                placeholder={t('settingAccount.oldPassword')}
                value={form.values?.oldPassword}
                onChange={form.handleChange}
                error={Boolean(form.errors.oldPassword)}
                helperText={form.errors.oldPassword}
                type='password'
              />
            </Box>
          </BaseRow>

          <BaseRow className='gap-[20px] mt-[20px]'>
            <Box className='w-full'>
              <InputLabel> {t('settingAccount.newPassword')}</InputLabel>
              <TextField
                name='password'
                placeholder={t('settingAccount.newPassword')}
                value={form.values?.password}
                onChange={form.handleChange}
                error={Boolean(form.errors.password)}
                helperText={form.errors.password}
                type='password'
              />
            </Box>
          </BaseRow>

          <BaseRow className='gap-[20px] mt-[20px]'>
            <Box className='w-full'>
              <InputLabel> {t('settingAccount.confirmNewPassword')}</InputLabel>
              <TextField
                name='confirmPassword'
                placeholder={t('settingAccount.confirmNewPassword')}
                value={form.values?.confirmPassword}
                onChange={form.handleChange}
                error={Boolean(form.errors.confirmPassword)}
                helperText={form.errors.confirmPassword}
                type='password'
              />
            </Box>
          </BaseRow>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button color='secondary' variant='contained' onClick={() => handleCancel()}>
          {t('cancel', { ns: 'admin/common' })}
        </Button>
        <Button
          color='primary'
          variant='contained'
          onClick={() => {
            form.handleSubmit();
          }}
        >
          {t('save', { ns: 'admin/common' })}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default memo(AccountSettingPasswordDialog);
